import * as XLSX from 'xlsx'

export type ImportedChargePart = { type: 'PARCELA' | 'BALAO'; baseAmount: string }
export type ImportUnitStatus = 'NOVA' | 'EXISTENTE' | 'DUPLICADA_NO_ARQUIVO' | 'AMBIGUA'

export interface ImportedScheduleCharge { competence: string; type: 'PARCELA' | 'BALAO'; baseAmount: string }
export interface ImportedUnitPreview {
  key: string; unitCode: string; customerName: string; email: string | null; displayFraction: string; referenceCub: string; referenceCompetence: string
  status: ImportUnitStatus; reason: string; charges: ImportedScheduleCharge[]
}

export function sortImportedCharges(charges: ImportedScheduleCharge[]) {
  return [...charges].sort((left, right) => left.competence.localeCompare(right.competence) || left.type.localeCompare(right.type))
}

const asPositiveNumber = (value: unknown) => {
  const number = typeof value === 'number' ? value : Number(String(value).replace(',', '.'))
  return Number.isFinite(number) && number > 0 ? number : null
}
const cell = (sheet: XLSX.WorkSheet, row: number, column: number) => sheet[XLSX.utils.encode_cell({ r: row, c: column })]
const cellValue = (sheet: XLSX.WorkSheet, row: number, column: number) => cell(sheet, row, column)?.v
const text = (value: unknown) => String(value ?? '').trim()
const isoMonth = (value: unknown) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-01`
  if (typeof value === 'number' && value > 20_000 && value < 80_000) {
    const date = XLSX.SSF.parse_date_code(value)
    if (date) return `${date.y}-${String(date.m).padStart(2, '0')}-01`
  }
  return null
}

/** The smallest literal addition is a parcel; every larger component is a balloon. */
export function splitBaseFormula(formula: string | undefined, displayedValue: unknown): ImportedChargePart[] | null {
  void displayedValue
  const source = (formula || '').replace(/^=/, '').replace(/\s/g, '')
  if (!/^\d+(?:[.,]\d+)?(?:\+\d+(?:[.,]\d+)?)+$/.test(source)) return null
  const values = source.split('+').map(value => Number(value.replace(',', '.')))
  if (values.some(value => !Number.isFinite(value) || value <= 0)) return null
  const smallest = Math.min(...values)
  if (values.filter(value => value === smallest).length !== 1) return null
  return values.map(value => ({ type: value === smallest ? 'PARCELA' : 'BALAO', baseAmount: value.toFixed(2) }))
}

function findCubHeader(sheet: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1')
  for (let row = range.s.r; row < range.e.r; row += 1) {
    const months: { column: number; competence: string }[] = []
    for (let column = range.s.c; column <= range.e.c; column += 1) {
      const competence = isoMonth(cellValue(sheet, row, column))
      if (competence) months.push({ column, competence })
    }
    if (months.length >= 6) return { row, months }
  }
  return null
}

const paymentMonthNumbers: Record<string, number> = { jan:1,fev:2,mar:3,abr:4,mai:5,jun:6,jul:7,ago:8,set:9,out:10,nov:11,dez:12 }
/** Reads the visible payment calendar (year row + month row), independently from CUB reference dates. */
function findPaymentMonths(sheet: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1')
  for (let row = range.s.r + 1; row <= range.e.r; row += 1) {
    let year: number | null = null
    const months = new Map<number,string>()
    for (let column = range.s.c; column <= range.e.c; column += 1) {
      const yearValue = Number(cellValue(sheet,row-1,column))
      if (Number.isInteger(yearValue) && yearValue >= 2000 && yearValue <= 2100) year=yearValue
      const month = paymentMonthNumbers[text(cellValue(sheet,row,column)).slice(0,3).toLowerCase()]
      if (year && month) months.set(column,`${year}-${String(month).padStart(2,'0')}-01`)
    }
    if (months.size >= 6) return months
  }
  return new Map<number,string>()
}

function median(values: number[]) {
  const sorted = [...values].sort((a, b) => a - b)
  return sorted[Math.floor(sorted.length / 2)] || 0
}

function referenceCompetence(sheet: XLSX.WorkSheet, row: number, header: { months: { column: number; competence: string }[] }) {
  const formula = cell(sheet, row, 5)?.f || ''
  const column = formula.match(/\$?([A-Z]+)\$?6[45]/)?.[1]
  const index = column ? XLSX.utils.decode_col(column) : -1
  return header.months.find(month => month.column === index)?.competence || header.months[0].competence
}

/** Reads blocks by structural markers: unit/name followed by fraction and reference CUB. */
export function buildImportPreview(sheet: XLSX.WorkSheet, existingUnitCodes: Iterable<string> = []): ImportedUnitPreview[] {
  const header = findCubHeader(sheet)
  if (!header) return []
  const paymentMonths = findPaymentMonths(sheet)
  const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1')
  const existing = new Set([...existingUnitCodes].map(code => text(code).toUpperCase()))
  const seen = new Set<string>()
  const previews: ImportedUnitPreview[] = []
  for (let row = header.row + 2; row < range.e.r; row += 1) {
    const unitCode = text(cellValue(sheet, row, 2)), customerName = text(cellValue(sheet, row, 3)), displayFraction = text(cellValue(sheet, row + 1, 2))
    const referenceCub = asPositiveNumber(cellValue(sheet, row + 1, 5))
    if (!unitCode || !customerName || !/^\d+(?:[./]\d+)?$/.test(unitCode) || !/\d+\s*\/\s*\d+/.test(displayFraction) || !referenceCub) continue
    const key = unitCode.toUpperCase()
    const rawCharges: { competence: string; parts: ImportedChargePart[]; scalar: boolean }[] = []
    let ambiguous = false
    for (const month of header.months) {
      const source = cell(sheet, row, month.column), amount = asPositiveNumber(source?.v)
      if (!amount) continue
      const split = splitBaseFormula(source?.f, source?.v)
      if (source?.f && source.f.includes('+') && !split) { ambiguous = true; continue }
      rawCharges.push({ competence: paymentMonths.get(month.column) || month.competence, parts: split || [{ type: 'PARCELA', baseAmount: amount.toFixed(2) }], scalar: !split })
    }
    const recurringAmount = median(rawCharges.filter(charge => charge.scalar).map(charge => Number(charge.parts[0].baseAmount)))
    const charges = rawCharges.flatMap(charge => charge.parts.map(part => ({ competence: charge.competence, type: charge.scalar && recurringAmount > 0 && Number(part.baseAmount) > recurringAmount * 1.5 ? 'BALAO' as const : part.type, baseAmount: part.baseAmount })))
    let status: ImportUnitStatus = 'NOVA', reason = 'Unidade nova, apta para confirmação posterior.'
    if (!charges.length || ambiguous) { status = 'AMBIGUA'; reason = !charges.length ? 'Nenhum valor-base identificável.' : 'Há fórmula com ajuste ou referência que exige revisão.' }
    else if (seen.has(key)) { status = 'DUPLICADA_NO_ARQUIVO'; reason = 'A mesma unidade já apareceu antes nesta planilha.' }
    else if (existing.has(key)) { status = 'EXISTENTE'; reason = 'A unidade já existe no Roly Financeiro; nenhum dado será alterado.' }
    seen.add(key)
    previews.push({ key, unitCode, customerName, email: text(cellValue(sheet, row + 1, 3)) || null, displayFraction, referenceCub: referenceCub.toFixed(2), referenceCompetence: referenceCompetence(sheet, row + 1, header), status, reason, charges })
  }
  return previews
}
